import React, { useState } from 'react';
import { Globe, Link2, Info, Tag, Package, ScanBarcode, Barcode, Zap } from 'lucide-react';
import { WEBSITE_OPTIONS, AMAZON_REGIONS, PRODUCT_SEARCH_CARD_STRINGS } from '../../constants/appConstants';
import styles from './ProductSearchCard.module.scss';

const ProductSearchCard = ({ 
  formData, 
  onFormChange, 
  onScrape, 
  loading,
  setError,
}) => {
  const [localFormData, setLocalFormData] = useState(formData);

  const handleChange = (e) => {
    const newData = {
      ...localFormData,
      [e.target.name]: e.target.value
    };
    setLocalFormData(newData);
    onFormChange(newData);
  };

  // A search only needs ONE of: (brand AND product), an OEM number, or an
  // ASIN number - any single one of these is enough to identify a product.
  // Brand/Product are no longer hard-required in the markup below so that
  // an OEM- or ASIN-only search can actually be submitted; this function is
  // what enforces "at least one valid way to identify a product" instead.
  const hasValidIdentifier = (data) => {
    const brand = (data.brand || '').trim();
    const product = (data.product || '').trim();
    const oem = (data.oem_number || '').trim();
    const asin = (data.asin_number || '').trim();
    return (!!brand && !!product) || !!oem || !!asin;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isShopify = localFormData.website === 'shopify';

    if (!isShopify && !hasValidIdentifier(localFormData)) {
      if (typeof setError === 'function') {
        setError(PRODUCT_SEARCH_CARD_STRINGS.ERRORS.MISSING_IDENTIFIER);
      }
      return;
    }

    const isAmazon = localFormData.website === 'amazon' || localFormData.website === '' || localFormData.website === 'allwebsite';
    const payload = {
      ...localFormData,
      amazon_country: isAmazon ? (localFormData.amazon_country || 'amazon.com') : '',
    };
    onScrape(payload);
  };

  const showAmazonRegion = localFormData.website === 'amazon' || localFormData.website === 'allwebsite';

  return (
    <div className={styles.productSearchCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderContent}>
          <h3 className={styles.cardTitle}>{PRODUCT_SEARCH_CARD_STRINGS.TITLE}</h3>
          <p className={styles.cardDescription}>{PRODUCT_SEARCH_CARD_STRINGS.DESCRIPTION}</p>
        </div>
      </div>
      
      <div className={styles.cardBody}>
        <form onSubmit={handleSubmit} className={styles.productSearchForm}>
          
          {/* Website Selection */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <Globe size={14} />
              {PRODUCT_SEARCH_CARD_STRINGS.LABELS.WEBSITE}
            </label>
            <div className={styles.formInputWrapper}>
              <select
                name="website"
                className={styles.formSelect}
                value={localFormData.website}
                onChange={handleChange}
                required
              >
                <option value="" disabled>Select Website</option>
                {WEBSITE_OPTIONS.map(opt => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    className={opt.value === 'shopify' ? styles.shopifyOption : undefined}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Amazon Region Field - shown immediately below Website when Amazon is selected */}
          {showAmazonRegion && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <Globe size={14} />
                {PRODUCT_SEARCH_CARD_STRINGS.LABELS.AMAZON_REGION}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.formInputWrapper}>
                <select
                  name="amazon_country"
                  className={styles.formSelect}
                  value={localFormData.amazon_country}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Select Region</option>
                  {AMAZON_REGIONS.map(region => (
                    <option key={region.value} value={region.value}>{region.label}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formHelp}>
                <Info size={14} />
                {PRODUCT_SEARCH_CARD_STRINGS.HELP.AMAZON_REGION}
              </div>
            </div>
          )}

          {/* Conditional Inputs based on Shopify selection */}
          {localFormData.website === 'shopify' ? (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <Link2 size={14} />
                <span>{PRODUCT_SEARCH_CARD_STRINGS.LABELS.STORE_URL}</span>
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.formInputWrapper}>
                <input
                  type="text"
                  name="store_url"
                  className={styles.formInput}
                  placeholder={PRODUCT_SEARCH_CARD_STRINGS.PLACEHOLDERS.STORE_URL}
                  value={localFormData.store_url}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className={styles.formHelp}>
                <Info size={14} />
                {PRODUCT_SEARCH_CARD_STRINGS.HELP.STORE_URL}
              </div>
            </div>
          ) : (
            <>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Tag size={14} />
                    <span>{PRODUCT_SEARCH_CARD_STRINGS.LABELS.BRAND}</span>
                  </label>
                  <div className={styles.formInputWrapper}>
                    <input
                      type="text"
                      name="brand"
                      className={styles.formInput}
                      placeholder={PRODUCT_SEARCH_CARD_STRINGS.PLACEHOLDERS.BRAND}
                      value={localFormData.brand}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Package size={14} />
                    <span>{PRODUCT_SEARCH_CARD_STRINGS.LABELS.PRODUCT}</span>
                  </label>
                  <div className={styles.formInputWrapper}>
                    <input
                      type="text"
                      name="product"
                      className={styles.formInput}
                      placeholder={PRODUCT_SEARCH_CARD_STRINGS.PLACEHOLDERS.PRODUCT}
                      value={localFormData.product}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <ScanBarcode size={14} />
                    {PRODUCT_SEARCH_CARD_STRINGS.LABELS.OEM_NUMBER}
                  </label>
                  <div className={styles.formInputWrapper}>
                    <input
                      type="text"
                      name="oem_number"
                      className={styles.formInput}
                      placeholder={PRODUCT_SEARCH_CARD_STRINGS.PLACEHOLDERS.OEM}
                      value={localFormData.oem_number}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Barcode size={14} />
                    {PRODUCT_SEARCH_CARD_STRINGS.LABELS.ASIN_NUMBER}
                  </label>
                  <div className={styles.formInputWrapper}>
                    <input
                      type="text"
                      name="asin_number"
                      className={styles.formInput}
                      placeholder={PRODUCT_SEARCH_CARD_STRINGS.PLACEHOLDERS.ASIN}
                      value={localFormData.asin_number}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formHelp}>
                <Info size={14} />
                {PRODUCT_SEARCH_CARD_STRINGS.HELP.IDENTIFIER}
              </div>
            </>
          )}

          <button 
            type="submit" 
            className={styles.searchButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className={styles.btnSpinner}></div>
                <span>{PRODUCT_SEARCH_CARD_STRINGS.BUTTONS.SEARCHING}</span>
              </>
            ) : (
              <>
                <Zap size={14} />
                <span>{PRODUCT_SEARCH_CARD_STRINGS.BUTTONS.START}</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductSearchCard;
